import { Component, EventEmitter, forwardRef, Input, Output, OnInit, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatSelectChange } from '@angular/material/select';
import { SelectOptions } from 'src/shared/models';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

@Component({
    selector: 'atom-input-select',
    templateUrl: './input-select.component.html',
    styleUrls: ['./input-select.component.scss'],
    providers: [
      {
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => InputSelectComponent),
        multi: true
      }
    ]
})
export class InputSelectComponent implements ControlValueAccessor, OnInit, OnDestroy, OnChanges {
  @Input() title: string = '';
  @Input() optionsList: SelectOptions[] = [];
  @Input() disabled: boolean = false;
  @Input() isMultiple: boolean = false;
  @Input() placeholder: string = '';
  @Input() value: string | string[] = '';
  @Output() changed = new EventEmitter<string>();
  @Output() multipleValuesChanged = new EventEmitter<string[]>();

  selectedValues: string[] = [];
  private langChangeSubscription?: Subscription;
  translatedTitle: string = '';
  translatedPlaceholder: string = '';
  selectedOptionsSector: any;

  constructor(private translateService: TranslateService) {}

  ngOnInit() {
    this.updateTitleTranslation();
    this.updatePlaceholderTranslation();
    this.langChangeSubscription = this.translateService.onLangChange.subscribe(() => {
      this.updateTitleTranslation();
      this.updatePlaceholderTranslation();
    });

    // Inicializar selectedValues si value es un array
    if (this.isMultiple && Array.isArray(this.value)) {
      this.selectedValues = [...this.value];
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    // Sincronizar selectedValues cuando value cambia desde el padre
    if (changes['value'] && this.isMultiple) {
      if (Array.isArray(this.value)) {
        this.selectedValues = [...this.value];
      } else {
        this.selectedValues = [];
      }
    }
  }

  ngOnDestroy() {
    if (this.langChangeSubscription) {
      this.langChangeSubscription.unsubscribe();
    }
  }

  get selectedOptions(): SelectOptions[] {
    return this.optionsList.filter(option => this.selectedValues.includes(option.value));
  }

  get selectedTitles(): string[] {
    return this.selectedOptions.map(option => this.translateText(option.title));
  }

  get displayValue(): string {
    if (this.isMultiple) {
      return this.selectedTitles.join(', ') || this.placeholder;
    }
    const selectedOption = this.optionsList.find(option => option.value === this.value);
    return selectedOption ? this.translateText(selectedOption.title) : this.placeholder;
  }

  private updateTitleTranslation() {
    if (this.title) {
      const translation = this.translateService.instant(this.title);
      this.translatedTitle = translation === this.title ? this.title : translation;
    }
  }

  private updatePlaceholderTranslation() {
    if (this.placeholder) {
      const translation = this.translateService.instant(this.placeholder);
      this.translatedPlaceholder = translation === this.placeholder ? this.placeholder : translation;
    }
  }

  private translateText(text: string): string {
    if (!text) return '';

    if (text.includes('|')) { // Si el texto contiene un pipe, se asume formato "key|{param1: value1, param2: value2}"
      const [key, paramsStr] = text.split('|');
      if (paramsStr) {
        try {
          const params = JSON.parse(paramsStr.trim());
          return this.translateService.instant(key.trim(), params);
        } catch (e) {
          console.warn('Error parseando parámetros de traducción:', e);
          return this.translateService.instant(key.trim());
        }
      }
      return this.translateService.instant(key.trim());
    }

    if (!isNaN(Number(text)) || /\d|%/.test(text)) {
      return text;
    }

    const translation = this.translateService.instant(text);
    return translation === text ? text : translation;
  }

  reset(): void {
    if (this.isMultiple) {
      this.selectedValues = [];
      this.multipleValuesChanged.emit([]);
      this.propagateMultipleValuesChange([]);
    } else {
      this.value = '';
      this.changed.emit('');
      this.propagateChange('');
    }
    this.propagateTouched();
  }

  private propagateChange: (value: string) => void = () => {};
  private propagateMultipleValuesChange: (value: string[]) => void = () => {};
  private propagateTouched: () => void = () => {};

  writeValue(value: string): void {
    this.value = value || this.translateText('SELECT.DEFAULT');
  }

  registerOnChange(fn: any): void {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.propagateTouched = fn;
  }

  onInput(event: MatSelectChange): void {
    const selectedValue = event.value;

    if (selectedValue) {
      this.value = selectedValue;
    } else {
      this.value = '';
    }
    // Solo propaga si es selección simple
    if (!this.isMultiple) {
      this.propagateChange(this.value as string);
      this.changed.emit(this.value as string);
    }
  }

  onBlur() {
    this.propagateTouched();
  }

  onSelectChange(event: MatSelectChange) {
    this.selectedValues = event.value;
    if (this.isMultiple) {
      this.propagateMultipleValuesChange(this.selectedValues);
      this.multipleValuesChanged.emit(this.selectedValues);
    }
  }

  removeOption(value: string) {
    this.selectedValues = this.selectedValues.filter(item => item !== value);
    this.propagateMultipleValuesChange(this.selectedValues);
    this.multipleValuesChanged.emit(this.selectedValues);
  }

  getOptionText(option: SelectOptions): string {
    return this.translateText(option.title);
  }

  getValue(): string {
    return typeof this.value === 'string' ? this.value : '';
  }
}
